import { useEffect, useState, useCallback } from 'react';
import { db } from '../config/firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';

/**
 * Hook to listen to worldObjectImageJobs collection for a specific user
 * Tracks job status changes in real-time
 * 
 * @param {string} userId - The user ID to filter jobs by
 * @returns {object} - { jobs: Map<jobId, jobData>, isLoading: boolean, error: Error | null }
 */
export function useWorldObjectJobs(userId) {
  const [jobs, setJobs] = useState(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Query for all jobs for this user
      const jobsQuery = query(
        collection(db, 'worldObjectImageJobs'),
        where('userId', '==', userId)
      );

      const unsubscribe = onSnapshot(
        jobsQuery,
        (snapshot) => {
          const jobsMap = new Map();
          snapshot.docs.forEach((doc) => {
            jobsMap.set(doc.id, {
              id: doc.id,
              ...doc.data(),
            });
          });
          setJobs(jobsMap);
          setIsLoading(false);
        },
        (err) => {
          console.error('[useWorldObjectJobs] Error listening to jobs:', err);
          setError(err);
          setIsLoading(false);
        }
      );

      return () => unsubscribe();
    } catch (err) {
      console.error('[useWorldObjectJobs] Setup error:', err);
      setError(err);
      setIsLoading(false);
    }
  }, [userId]);

  return { jobs, isLoading, error };
}

/**
 * Hook to listen to worldObjectImageJobs for a specific book
 * Useful for tracking generation progress for a specific book
 * 
 * @param {string} bookId - The book ID to filter jobs by
 * @returns {object} - { jobs: Map<jobId, jobData>, isLoading: boolean, error: Error | null }
 */
export function useWorldObjectJobsByBook(bookId) {
  const [jobs, setJobs] = useState(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!bookId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Query for all jobs for this book
      const jobsQuery = query(
        collection(db, 'worldObjectImageJobs'),
        where('bookId', '==', bookId)
      );

      const unsubscribe = onSnapshot(
        jobsQuery,
        (snapshot) => {
          const jobsMap = new Map();
          snapshot.docs.forEach((doc) => {
            jobsMap.set(doc.id, {
              id: doc.id,
              ...doc.data(),
            });
          });
          setJobs(jobsMap);
          setIsLoading(false);
        },
        (err) => {
          console.error('[useWorldObjectJobsByBook] Error listening to jobs:', err);
          setError(err);
          setIsLoading(false);
        }
      );

      return () => unsubscribe();
    } catch (err) {
      console.error('[useWorldObjectJobsByBook] Setup error:', err);
      setError(err);
      setIsLoading(false);
    }
  }, [bookId]);

  return { jobs, isLoading, error };
}

/**
 * Hook to listen to PENDING worldObjectImageJobs for a specific book
 * Optimized for real-time loading UI - only tracks jobs that are still pending
 * Maps objectId -> jobData for quick lookup when rendering cards
 * 
 * @param {string} bookId - The book ID to filter jobs by
 * @returns {object} - { pendingJobs: Map<objectId, jobData>, isLoading: boolean, error: Error | null }
 */
export function useWorldObjectPendingJobs(bookId) {
  const [pendingJobs, setPendingJobs] = useState(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!bookId) {
      setIsLoading(false);
      setPendingJobs(new Map());
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Query for pending jobs for this book
      // NOTE: Requires composite index on (bookId, status) - see firestore.indexes.json
      const jobsQuery = query(
        collection(db, 'worldObjectImageJobs'),
        where('bookId', '==', bookId),
        where('status', '==', 'pending')
      );

      const unsubscribe = onSnapshot(
        jobsQuery,
        (snapshot) => {
          const jobsMap = new Map();
          snapshot.docs.forEach((doc) => {
            const jobData = {
              id: doc.id,
              ...doc.data(),
            };
            // Map by objectId for quick lookup in card rendering
            // @ts-ignore - objectId comes from Firestore document data
            jobsMap.set(jobData.objectId, jobData);
          });
          setPendingJobs(jobsMap);
          setIsLoading(false);
          
          if (jobsMap.size > 0) {
            console.log('[useWorldObjectPendingJobs] Tracking', jobsMap.size, 'pending job(s)');
          }
        },
        (err) => {
          console.error('[useWorldObjectPendingJobs] Error listening to pending jobs:', err);
          setError(err);
          setIsLoading(false);
        }
      );

      return () => {
        unsubscribe();
      };
    } catch (err) {
      console.error('[useWorldObjectPendingJobs] Setup error:', err);
      setError(err);
      setIsLoading(false);
    }
  }, [bookId]);

  return { pendingJobs, isLoading, error };
}
